import { useQuery } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  createContext,
  useMemo,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import { knowledgeCatalogQuery } from 'query/knowledge';
import { KizenError } from 'services/errors';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import { getCatalogDetailSubroutes } from './navigation';

type CatalogDetailPageValue = ReturnType<typeof useCatalogDetail>;

const CatalogDetailPageContext = createContext<
  CatalogDetailPageValue | undefined
>(undefined);

const useCatalogDetail = () => {
  const { catalogId } = useParams<{ catalogId: string }>();

  const queryOptions = useMemo(
    () =>
      knowledgeCatalogQuery(catalogId, {
        staleTime: 0,
        enabled: !!catalogId,
      }),
    [catalogId]
  );

  const query = useQuery(queryOptions);
  const catalog = query.data;

  const subroutes = useMemo(() => {
    const routes = catalogId
      ? Object.values(getCatalogDetailSubroutes(catalogId))
      : [];

    return routes.filter(
      (route) => !route.hideForDefault || !catalog?.is_default
    );
  }, [catalogId, catalog?.is_default]);

  return {
    catalog,
    query,
    subroutes,
  };
};

export const useCatalogDetailPage = () => {
  const ctx = useContext(CatalogDetailPageContext);

  if (!ctx) {
    throw Error(
      'useCatalogDetailPage must be used within CatalogDetailProvider'
    );
  }

  return ctx;
};

export const CatalogDetailProvider = ({ children }: PropsWithChildren) => {
  const ctx = useCatalogDetail();
  const { error, isError } = ctx.query;

  if (isError) {
    throw new KizenError(getOriginalErrorStatusCode(error) ?? 404);
  }

  return (
    <CatalogDetailPageContext.Provider value={ctx}>
      {children}
    </CatalogDetailPageContext.Provider>
  );
};
